import { ReactNode } from "react";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";

export interface TableColumn<T = any> {
  header: string;
  accessor: keyof T | ((row: T) => ReactNode);
  align?: "left" | "center" | "right";
  className?: string;
}

export interface TableProps<T = any> {
  columns: TableColumn<T>[];
  data: T[];
  footer?: ReactNode;
  emptyMessage?: string;
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string;
  className?: string;
}

export function Table<T extends Record<string, any>>({
  columns,
  data,
  footer,
  emptyMessage = "No data available",
  isLoading = false,
  onRowClick,
  rowClassName,
  className = "",
}: TableProps<T>) {
  const getCellValue = (row: T, column: TableColumn<T>) => {
    if (typeof column.accessor === "function") {
      return column.accessor(row);
    }
    return row[column.accessor];
  };

  const getAlignmentClass = (align?: "left" | "center" | "right") => {
    switch (align) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      default:
        return "text-left";
    }
  };

  if (isLoading) {
    return (
      <div className={cn("overflow-x-auto", className)}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navigator-primary"></div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn("overflow-x-auto", className)}>
        <div className="flex items-center justify-center py-12">
          <Typography variant="body" color="light">{emptyMessage}</Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("overflow-x-auto", className)}>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            {columns.map((column, index) => (
              <th
                key={index}
                className={cn("py-3 px-4", getAlignmentClass(column.align), column.className)}
              >
                <Typography variant="body" weight="semibold" color="text-gray-700" as="span">
                  {column.header}
                </Typography>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              onClick={() => onRowClick?.(row)}
              className={cn(
                "border-b border-gray-100 hover:bg-gray-50 transition-colors",
                onRowClick ? "cursor-pointer" : "",
                rowClassName?.(row),
              )}
            >
              {columns.map((column, colIndex) => (
                <td
                  key={colIndex}
                  className={cn("py-3 px-4 text-sm", getAlignmentClass(column.align), column.className)}
                >
                  {getCellValue(row, column)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
    </div>
  );
}
